import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginEmail = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("veraso_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const registerEmail = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("veraso_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {}
    localStorage.removeItem("veraso_token");
    setUser(null);
  };

  /**
   * setSession — used by the invitation-accept flow to log the user in
   * immediately after they choose a password on /kutsu/:token, so they land
   * inside the app in one click.
   */
  const setSession = useCallback(({ token, user }) => {
    if (token) {
      try { localStorage.setItem("veraso_token", token); } catch { /* noop */ }
    }
    if (user) setUser(user);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, setSession, loading, loginEmail, registerEmail, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
